import React, { useState, useMemo } from 'react';
import { IonPage, IonContent, useIonRouter, useIonToast } from '@ionic/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'react-router-dom';
import { useResetPassword } from '@/hooks/useAuth';
import '@/theme/login.css';

const schema = z.object({
  password: z
    .string()
    .min(1, 'Parola este obligatorie')
    .min(8, 'Parola trebuie să aibă minim 8 caractere'),
  password_confirm: z.string().min(1, 'Confirmarea parolei este obligatorie'),
}).refine((data) => data.password === data.password_confirm, {
  message: 'Parolele nu se potrivesc',
  path: ['password_confirm'],
});

type FormData = z.infer<typeof schema>;

const ResetPasswordPage: React.FC = () => {
  const router = useIonRouter();
  const location = useLocation();
  const mutation = useResetPassword();
  const [presentToast] = useIonToast();
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') ?? '';
  }, [location.search]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', password_confirm: '' },
  });

  const passwordValue = watch('password');
  const confirmValue = watch('password_confirm');

  const onSubmit = async (data: FormData) => {
    if (!token) {
      void presentToast({
        message: 'Link invalid. Solicitați un nou link de resetare.',
        duration: 4000,
        color: 'danger',
        position: 'top',
      });
      return;
    }
    try {
      await mutation.mutateAsync({ token, password: data.password });
      setSuccess(true);
    } catch {
      void presentToast({
        message: 'Eroare la resetarea parolei. Încercați din nou.',
        duration: 3000,
        color: 'danger',
        position: 'top',
      });
    }
  };

  const isPending = isSubmitting || mutation.isPending;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="login-split">
          {/* ── Left: Form Panel ── */}
          <div className="login-form-panel">
            <div className="login-form-inner">
              {success ? (
                <div className="login-animate login-animate--1">
                  <div className="login-header">
                    <h1 className="login-title">
                      Parola a fost resetată.
                    </h1>
                    <p className="login-subtitle">
                      Puteți acum să vă autentificați cu noua parolă.
                    </p>
                  </div>
                  <div className="login-actions">
                    <button
                      type="button"
                      className="login-btn login-btn--primary"
                      onClick={() => router.push('/login', 'root', 'replace')}
                    >
                      Autentificare
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="login-header login-animate login-animate--1">
                    <h1 className="login-title">
                      Resetare parolă.
                      <br />
                      Introduceți noua parolă.
                    </h1>
                    <p className="login-subtitle">
                      Alegeți o parolă sigură cu cel puțin 8 caractere
                    </p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="login-fields">
                      {/* New Password */}
                      <div
                        className={`login-field login-animate login-animate--2${
                          passwordValue ? ' login-field--has-value' : ''
                        }`}
                      >
                        <input
                          {...register('password')}
                          type={showPassword ? 'text' : 'password'}
                          className="login-field__input"
                          placeholder=" "
                          autoComplete="new-password"
                          autoFocus
                        />
                        <label className="login-field__label">Parolă nouă</label>
                        <button
                          type="button"
                          className="login-field__toggle-pw"
                          onClick={() => setShowPassword(!showPassword)}
                          tabIndex={-1}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            {showPassword ? (
                              <>
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </>
                            ) : (
                              <>
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </>
                            )}
                          </svg>
                        </button>
                        {errors.password && (
                          <span className="login-field__error">{errors.password.message}</span>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div
                        className={`login-field login-animate login-animate--3${
                          confirmValue ? ' login-field--has-value' : ''
                        }`}
                      >
                        <input
                          {...register('password_confirm')}
                          type={showPassword ? 'text' : 'password'}
                          className="login-field__input"
                          placeholder=" "
                          autoComplete="new-password"
                        />
                        <label className="login-field__label">Confirmă parola</label>
                        <span className="login-field__icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </span>
                        {errors.password_confirm && (
                          <span className="login-field__error">{errors.password_confirm.message}</span>
                        )}
                      </div>
                    </div>

                    <div className="login-actions login-animate login-animate--4">
                      <button
                        type="submit"
                        className="login-btn login-btn--primary"
                        disabled={isPending}
                      >
                        {isPending && <span className="login-spinner" />}
                        Resetare parolă
                      </button>
                    </div>
                  </form>

                  <div style={{ textAlign: 'center', marginTop: 32 }} className="login-animate login-animate--5">
                    <a
                      className="login-forgot"
                      href="/login"
                      onClick={(e) => {
                        e.preventDefault();
                        router.push('/login', 'back');
                      }}
                      style={{ textAlign: 'center', display: 'inline' }}
                    >
                      Înapoi la autentificare
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Right: Brand Panel ── */}
          <div className="login-brand-panel">
            <div className="login-brand-panel__shapes">
              <div className="login-decor login-decor--1" />
              <div className="login-decor login-decor--2" />
              <div className="login-decor login-decor--3" />
              <div className="login-decor login-decor--4" />
              <div className="login-decor login-decor--5" />
            </div>
            <div className="login-brand-content">
              <div className="login-brand-logo">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h2 className="login-brand-name">Betel Gestiune</h2>
              <p className="login-brand-tagline">
                Platforma completă pentru administrarea eficientă a comunității bisericii
              </p>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ResetPasswordPage;
